import type { JSX } from "solid-js";
import { cn } from "@time-tracker/shared";

type IllustrationProps = JSX.SvgSVGAttributes<SVGSVGElement> & {
  class?: string;
};

/**
 * Theme-aware decorative illustrations for empty states and headers.
 *
 * They lean on `currentColor` plus the accent token so they adapt to every
 * theme automatically. Marked `aria-hidden` — they're purely decorative and
 * the surrounding copy carries the meaning. Strokes are animation-light;
 * the subtle sweep respects `prefers-reduced-motion` via the global guard.
 */

/** An open clock with a soft sweeping hand — the canonical "no time tracked" cue. */
export function ClockEmptyIllustration(props: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      aria-hidden="true"
      class={cn("text-accent", props.class)}
      {...props}
    >
      {/* Soft halo */}
      <circle cx="80" cy="80" r="58" fill="currentColor" opacity="0.06" />
      <circle cx="80" cy="80" r="46" fill="currentColor" opacity="0.05" />
      {/* Dial */}
      <circle cx="80" cy="80" r="40" stroke="currentColor" stroke-width="3" opacity="0.55" />
      {/* Tick marks */}
      <g stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity="0.35">
        <line x1="80" y1="44" x2="80" y2="51" />
        <line x1="80" y1="109" x2="80" y2="116" />
        <line x1="44" y1="80" x2="51" y2="80" />
        <line x1="109" y1="80" x2="116" y2="80" />
      </g>
      {/* Hands */}
      <g stroke="currentColor" stroke-linecap="round">
        <line x1="80" y1="80" x2="80" y2="56" stroke-width="4" opacity="0.85" />
        <line x1="80" y1="80" x2="98" y2="88" stroke-width="3.5" opacity="0.85">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 80 80"
            to="360 80 80"
            dur="8s"
            repeatCount="indefinite"
          />
        </line>
      </g>
      <circle cx="80" cy="80" r="4" fill="currentColor" />
    </svg>
  );
}

/** A stack of horizontal bars suggesting an empty timesheet / report. */
export function TimesheetEmptyIllustration(props: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      aria-hidden="true"
      class={cn("text-accent", props.class)}
      {...props}
    >
      <circle cx="80" cy="80" r="56" fill="currentColor" opacity="0.06" />
      <rect
        x="44"
        y="40"
        width="72"
        height="80"
        rx="10"
        stroke="currentColor"
        stroke-width="3"
        opacity="0.5"
      />
      <g fill="currentColor">
        <rect x="56" y="56" width="34" height="6" rx="3" opacity="0.55" />
        <rect x="56" y="74" width="48" height="6" rx="3" opacity="0.35" />
        <rect x="56" y="92" width="28" height="6" rx="3" opacity="0.45" />
      </g>
      {/* Mini clock badge */}
      <circle cx="110" cy="110" r="16" fill="currentColor" opacity="0.12" />
      <circle cx="110" cy="110" r="13" stroke="currentColor" stroke-width="2.5" opacity="0.7" />
      <g stroke="currentColor" stroke-width="2.5" stroke-linecap="round" opacity="0.9">
        <line x1="110" y1="110" x2="110" y2="103" />
        <line x1="110" y1="110" x2="116" y2="113" />
      </g>
    </svg>
  );
}

/** A folder with a clock accent for the projects empty state. */
export function ProjectsEmptyIllustration(props: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      aria-hidden="true"
      class={cn("text-accent", props.class)}
      {...props}
    >
      <circle cx="80" cy="80" r="56" fill="currentColor" opacity="0.06" />
      <path
        d="M42 58a8 8 0 0 1 8-8h20l8 10h32a8 8 0 0 1 8 8v40a8 8 0 0 1-8 8H50a8 8 0 0 1-8-8V58Z"
        stroke="currentColor"
        stroke-width="3"
        opacity="0.5"
        stroke-linejoin="round"
      />
      <circle cx="100" cy="92" r="15" fill="currentColor" opacity="0.12" />
      <circle cx="100" cy="92" r="12" stroke="currentColor" stroke-width="2.5" opacity="0.75" />
      <g stroke="currentColor" stroke-width="2.5" stroke-linecap="round" opacity="0.95">
        <line x1="100" y1="92" x2="100" y2="85" />
        <line x1="100" y1="92" x2="106" y2="95" />
      </g>
    </svg>
  );
}
